import React, { useState, useEffect } from 'react';
import { AlertTriangle, WashingMachine, Sofa } from 'lucide-react';
import { safeFetch } from '../utils/safeFetch';
import { API_URL } from '../config';
import { CompactListSkeleton } from './Skeleton';

const DepartmentSelector = ({ userId, selectedDepartments, onChange, disabled = false }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Загрузка всех подразделений
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      setError('');
      
      try {
        const token = localStorage.getItem('token');
        const data = await safeFetch(`${API_URL}/api/departments/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setDepartments(data);
      } catch (err) {
        console.error('Ошибка загрузки подразделений:', err);
        setError(err.message || 'Ошибка загрузки подразделений');
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, []);

  // Обработка изменения выбора подразделения
  const handleDepartmentChange = (departmentId, checked) => {
    let updatedDepartments;
    
    if (checked) {
      updatedDepartments = [...selectedDepartments, departmentId];
    } else {
      updatedDepartments = selectedDepartments.filter(id => id !== departmentId);
    }
    
    onChange(updatedDepartments);
  };

  // Функция для получения названия типа заказа
  const getOrderTypeName = (orderType) => {
    switch (orderType) {
      case 'washing':
        return 'Стирка';
      case 'dry_cleaning':
        return 'Химчистка';
      default:
        return 'Не указан';
    }
  };

  // Функция для получения иконки типа заказа
  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'washing':
        return <WashingMachine className="w-3 h-3" />;
      case 'dry_cleaning':
        return <Sofa className="w-3 h-3" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="py-2">
        <CompactListSkeleton count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-200 rounded-lg px-4 py-3">
        <AlertTriangle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {departments.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm">Нет доступных подразделений</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto p-1">
          {departments.map((department) => (
            <div key={department.id} className="flex items-center">
              <input
                type="checkbox"
                id={`dept-${department.id}`}
                checked={selectedDepartments.includes(department.id)}
                onChange={(e) => handleDepartmentChange(department.id, e.target.checked)}
                disabled={disabled}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor={`dept-${department.id}`}
                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >
                <div className="flex items-center">
                  <span>{department.name}</span>
                  {/* Отображение типов заказов */}
                  {Array.isArray(department.order_types) && department.order_types.length > 0 && (
                    <div className="flex flex-wrap gap-1 ml-2">
                      {department.order_types.map((type) => (
                        <span key={type} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {getOrderTypeIcon(type)}
                          <span className="ml-0.5">{getOrderTypeName(type)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {!department.is_active && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(неактивно)</span>
                )}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartmentSelector;
